import { useCallback, useLayoutEffect, useState } from "react";

export type ColorMode = "light" | "dark" | "auto";

// Kept apart from AdminLTE's own `lte-theme` key, whose script is switched off in index.html.
export const COLOR_MODE_KEY = "portal.colorMode";

const read = (): ColorMode => {
  try {
    const v = localStorage.getItem(COLOR_MODE_KEY);
    return v === "dark" || v === "auto" ? v : "light";
  } catch {
    return "light";
  }
};

const prefersDark = () => window.matchMedia("(prefers-color-scheme: dark)").matches;

/**
 * Light/dark, applied by each layout (console, sign-in, public) on mount and taken off on unmount.
 * Only the console offers the switch (user menu); the signed-out pages follow what was chosen there.
 */
export const useColorMode = () => {
  const [mode, setModeState] = useState<ColorMode>(read);

  useLayoutEffect(() => {
    const root = document.documentElement;
    const apply = () => {
      const resolved = mode === "auto" ? (prefersDark() ? "dark" : "light") : mode;
      root.setAttribute("data-bs-theme", resolved);
      root.style.colorScheme = resolved;
    };
    apply();
    const media = window.matchMedia("(prefers-color-scheme: dark)");
    media.addEventListener("change", apply);
    return () => {
      media.removeEventListener("change", apply);
      root.removeAttribute("data-bs-theme");
      root.style.removeProperty("color-scheme");
    };
  }, [mode]);

  const setMode = useCallback((next: ColorMode) => {
    try {
      localStorage.setItem(COLOR_MODE_KEY, next);
    } catch {
      // Not remembered across visits; still applied now.
    }
    setModeState(next);
  }, []);

  return { mode, setMode };
};
